import React, { useContext, useState, useEffect, useRef } from 'react';
import styled from 'styled-components/macro';
import _ from 'lodash';
import LessonsContext from '@mirinae/lessons/modules/contexts/lessons';
import { UserContext } from '@mirinae/react-auth';
import { RotationSpinner } from '@mirinae/react-ui';
import DeleteIcon from '@material-ui/icons/DeleteForeverOutlined';
import ShowIcon from '@material-ui/icons/VisibilityOffOutlined';
import HideIcon from '@material-ui/icons/VisibilityOutlined';
import DuplicateIcon from '@material-ui/icons/FileCopyOutlined';
import FormatPaintIcon from '@material-ui/icons/FormatPaintOutlined';
import UpdateIcon from '@material-ui/icons/UpdateOutlined';
import SaveIcon from '@material-ui/icons/SaveOutlined';
import CloudDownloadIcon from '@material-ui/icons/CloudDownloadOutlined';
import PlayArrowIcon from '@material-ui/icons/PlayArrowOutlined';
import AutorenewOutlinedIcon from '@material-ui/icons/AutorenewOutlined';
import EditIcon from '@material-ui/icons/EditOutlined';
import TextInput from '@mirinae/lessons/editor/components/ui/TextInput';
import { apiServer } from '@mirinae/shared/defines/paths';
import { groupChoices, statusChoices, statusSelectOptions } from '@mirinae/lessons/editor/modules/contexts/editor';
import { EmptyList } from '@mirinae/lessons/components/mylessons/GroupComponents';
import { langsSelectList } from '@mirinae/shared/il8n';

const ListWrapper = styled.div`
    display: flex;
    flex-direction: column;
    font-family: Arial;
    font-size: 11px;
    border-collapse: separate;
    border-spacing: 2px 4px;
    margin-bottom: 46px;
`;

const List = styled.div`
    max-height: 700px;
    overflow: auto;
    
    & thead th {
        position: sticky; 
        top: 0; 
        z-index: 1;
        background-color: #f3f3f3;
    }
`;

const ListTable = styled.table`
    border-collapse: collapse;
    width: 100%;
    & th, td {
        padding: 2px 4px;
    }
`;

const ListHeader = styled.thead`
    font-weight: bold;
    > tr {
        padding-bottom: 3px;
        border-bottom: thin solid #b5b5b5;
    }
`;

const ListBody = styled.tbody`   
`;

const ColTitleHeader = styled.th`
    cursor: pointer;
    text-align: left;
    span {
        font-size: 14px;
        padding-left: 2px;
    }
`;

const ListRow = styled.tr`
    // cursor: pointer;
  background-color: ${props => (props.hover ? '#eee' : 'inherit')};
`;

const ListField = styled.td`
`;

const ListFieldWithMenu = styled(ListField)`
    cursor: pointer;
`;

const EditableTitle = styled.input`
    border: none;
    font-size: 11px;
    background: none;
    width: 180px;
    ${props => props.lesson.replaced && 'text-decoration: line-through;'}
`;

const DateCell = styled.td`
`;

const CheckboxCell = styled.td`
`;

const OpenClose = styled.div`
    > span {
        font-family: Webdings;
        font-size: 18px;
        cursor: pointer;
    }
`;
const Tools = styled.td`
    //svg { transform: scale(0.7); }
    svg { font-size: 16px; }
    text-align: right;
`;

const Editing = styled.td`
    color: red;
    cursor: pointer;
`;

const ToolButton = styled.span`
    cursor: ${props => (props.disable ? 'inherit' : 'pointer')};
    max-width: 60px;
    // margin: 2px 0px;
    color: ${props => (props.disable ? '#bbb' : '#6a6a6a')};
    margin-right: 6px;
`;

const ListSelect = styled.select`
    font-size: 11px;
    color: #3c3c3b;
    border-color: #a7a7a7;
    background-color: #f0f8ff;
`;

const ListTools = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: end;
`;

export const Select = ({ selection, options, optGroups, onChange, style }) => (
    <ListSelect value={selection} style={style} onChange={onChange}>
x        { options && options.map((o, i) => <option key={i} disabled={o.disabled} value={o.value || o}>{typeof o === 'string' ? o : o.label}</option>) }
        { optGroups && optGroups.map(og => (
            <optgroup key={og.group} label={og.group}>
                { og.options.map((o, i) => <option key={i} value={JSON.stringify(o.includes)}>{o.label}</option>) }
            </optgroup>
        ))}
    </ListSelect>
);

const FilterSwitches = styled.div`
    margin-right: 36px;
    display: flex;
    align-items: center;
    & > span:first-child {
        padding-right: 10px;
    }
    select + select {
        margin-left: 10px;
    }
`;

const Checkbox = styled.input`
    margin: 0 6px 0 16px;
    accent-color: #d9d9d9;
`;

let listOpen = true;

const displayInit = { group: 'All groups', status: 'Creating', private: true, allPrivate: false, archived: false, language: 'en', others: true }; // 'Ready': false,
const displayGroupOptions = {
    optGroups: [
        { group: 'Group', options: [
            { label: 'All groups', includes: groupChoices }].concat(groupChoices.map(c => ({ label: c, includes: c }))) },
        { group: 'Categories', options: [
            { label: 'Grammar', includes: ['Starter', 'Beginner', 'Intermediate', 'Advanced'] },
            { label: 'Culture', includes: ['Idioms', 'Neologisms', 'Proverbs'] }] }],
};

const displayLanguageOptions = {
    options: Object.entries(langsSelectList).map(([k, v]) => ({ label: v, value: k }))
};

const displayStatusOptions = {
    optGroups: [
        { group: 'Work sets', options: [
            { label: 'Planned', includes:['Planned'] },
            { label: 'Creating', includes:['Specifying', 'Ready for edit', 'Editing', 'Ready for review', 'Reviewing', 'Ready for test', 'Testing'] },
            { label: 'Reviewing', includes:['Ready for review', 'Reviewing', 'Ready for test', 'Testing', 'Final review'] },
            { label: 'Release admin', includes:['Final review', 'Coming soon', 'Published'] },
            { label: 'In production', includes:['Coming soon', 'Published'] },
            { label: 'Non-templates', includes:['Coming soon', 'Published', 'Planned', 'Specifying', 'Ready for edit', 'Editing', 'Ready for review', 'Reviewing', 'Ready for test', 'Testing', 'Final review'] },
            { label: 'Templates', includes:['Template'] }] },
        { group: 'Status', options: [
            { label: 'All statuses', includes: statusChoices }].concat(statusChoices.map(c => ({ label: c, includes: c }))) }],
};

export const sortInit = { title: -1, number: -1, group: -1, status: -1, private: -1, archived: -1, editor: -1, updated: -1, creator: -1, created: -1 };
const sortAccessor = (field) => (
    { number: l => (Number.isNaN(Number.parseInt(l.lessonNode.number, 10)) ? 1e6 : Number.parseInt(l.lessonNode.number, 10)),
        group: l => l.lessonNode.group,
        status: l => l.lessonNode.status,
        private: l => (l.private ? 'a' : 'b'),
        archived: l => (l.archived ? 'a' : 'b'),
        editor: l => (l.editor ? l.editor.editedBy.name : '?'),
        creator: l => (l.editor && l.editor.createdBy ? l.editor.createdBy.name : '?'),
    }[field] || (l => l[field])
);

const LessonList = () => {
    const lessons = useContext(LessonsContext);
    const [searchString, setSearchString] = useState('');
    const [lessonList, setLessonList] = useState([]);
    const [display, setDisplay] = useState(displayInit);
    const [userContext] = useContext(UserContext);
    const { user } = userContext;
    const isAdmin = user && user.role.match(/admin|superadmin|superuser/i);
    const isLessonCreator = user && user.role.match(/admin|superadmin|superuser|lessoncreator/i);

    const sort = useRef('updated');
    const [open, setOpen] = useState(false);
    const [order, setOrder] = useState(sortInit);
    const [hover, setHover] = useState(false);
    const editingEl = useRef(null);

    useEffect(() => {
        const disp = localStorage.getItem('lesson-list-display');
        const displaySettings = disp ? JSON.parse(disp) : displayInit;
        setDisplay(displaySettings);
        let srt; try { srt = JSON.parse(localStorage.getItem('lesson-list-sort')); } finally { /* */ }
        srt = srt || { field: 'updated', order: { ...sortInit } };
        setOrder(srt.order);
        sort.current = srt.field;
        sortLessons(sort.current);
    }, []);

    useEffect(() => {
        sortLessons(sort.current);
    }, [lessons.allLessons]);

    useEffect(() => {
        sortLessons(sort.current);
    }, [searchString]);

    useEffect(() => {
        if (!lessons.adminListContextMenu.open)
            setHover(null);
    }, [lessons.adminListContextMenu.open]);

    const sortLessons = (field) => {
        const searchIn = (l) => (Object.values(l.lessonNode).toString() + Object.values(l).toString() + (l.editor ? l.editor.editedBy.name : '?') + (l.editor && l.editor.createdBy ? l.editor.createdBy.name : '?')).toLowerCase();
        const filteredLessons = lessons.allLessons.filter(l => searchString === '' || searchIn(l).indexOf(searchString.toLowerCase()) >= 0);
        setLessonList(filteredLessons.sort((l1, l2) => order[field] * (sortAccessor(field)(l1) > sortAccessor(field)(l2) ? 1 : -1)));
        sort.current = field;
    };

    const sortBy = (field) => {
        order[field] *= -1;
        setOrder(order);
        sortLessons(field);
        localStorage.setItem('lesson-list-sort', JSON.stringify({ field, order }));
    };

    const displayChange = (filter, value, options = {}) => {
        display[filter] = value;
        setDisplay({ ...display });
        localStorage.setItem('lesson-list-display', JSON.stringify(display));
        if (options.reload)
            lessons.loadLessonLists({ language: display.language });
    };

    const DisplayFilter = () => (
        <FilterSwitches>
            <span>Show:</span>
            <Select selection={display.group} {...displayGroupOptions} onChange={e => displayChange('group', e.target.value)} />
            <Select selection={display.status} {...displayStatusOptions} onChange={e => displayChange('status', e.target.value)} />
            <Select selection={display.language} {...displayLanguageOptions} onChange={e => displayChange('language', e.target.value, { reload: true })} />
            <Checkbox type="checkbox" checked={display.others} onChange={e => displayChange('others', e.target.checked)} />
            <span>Regular</span>
            <Checkbox type="checkbox" checked={display.private} onChange={e => displayChange('private', e.target.checked)} />
            <span>Private</span>
            { isLessonCreator && (
                <>
                    <Checkbox type="checkbox" checked={display.allPrivate} onChange={e => displayChange('allPrivate', e.target.checked, { reload: true })} />
                    <span>All private</span>
                    <Checkbox type="checkbox" checked={display.myAssigns} onChange={e => displayChange('myAssigns', e.target.checked)} />
                    <span>Assigns</span>
                    <Checkbox type="checkbox" checked={display.replaced} onChange={e => displayChange('replaced', e.target.checked)} />
                    <span>Replaced</span>
                </>
            )}
            <Checkbox type="checkbox" checked={display.archived} onChange={e => displayChange('archived', e.target.checked, { reload: true })} />
            <span>Archived</span>
        </FilterSwitches>
    );

    const displayFilter = (lesson) => {
        let show = (display.others && !lesson.archived && !lesson.private)
            || (display.private && lesson.private && ((lesson.editor.createdBy && lesson.editor.createdBy._id === user._id) || (lesson.editor.assignedTo && lesson.editor.assignedTo._id === user._id) || display.allPrivate))
            || (display.myAssigns && lesson.editor.assignedTo && lesson.editor.assignedTo.assignedBy && lesson.editor.assignedTo.assignedBy._id === user._id)
            || (display.archived && lesson.archived);
        show = show && (display.group === 'All groups' || display.group.indexOf(lesson.lessonNode.group) >= 0);
        show = show && (display.status.indexOf(lesson.lessonNode.status) >= 0);
        show = show && (lesson.replaced ? display.replaced : true);
        return show;
    };

    const edit = (lesson) => {
        lessons.editLesson(lesson);
    };

    const fieldChanged = (e, lesson, field) => {
        if (lesson[field] !== e.target.value) {
            lessons.updateLessonNodeField(lesson, field, e.target.value);
        }
    };

    const flagChanged = (e, lesson, flag) => {
        lesson[flag] = e.target.checked;
        lessons.changeLessonFlag(lesson, flag, e.target.checked);
        setLessonList(prev => prev.map(lsn => lsn));
    };

    const titleChanged = (e, lesson) => {
        if (lesson.title !== e.target.value) {
            lesson.title = e.target.value;
            lessons.updateLessonNodeField(lesson, 'title', e.target.value);
        }
    };

    const assignEditor = (e, lesson) => {
        lessons.assignEditor(lesson, lessons.editors.find(ed => ed._id === e.target.value), user);
    };

    const onContextMenu = (e, lesson) => {
        e.preventDefault();
        e.stopPropagation();
        lessons.update({ adminListContextMenu: { open: true, lesson, e, anchorEl: e.target } });
    };

    const localDate = (dateString) => { // mild hack
        const d = new Date(dateString);
        const local = new Date(d.valueOf() - d.getTimezoneOffset() * 60 * 1000).toISOString().replace('T', ' ');
        return local.substring(0, local.length - 8);
    };

    const shortLocalDate = (datestring) => { const ld = localDate(datestring); return ld.substring(0, ld.length - 6); };

    const ColTitle = ({ field, children }) => (
        <ColTitleHeader onClick={() => sortBy(field)}>
            {children}
            { sort.current === field && <span>{order[field] < 0 ? '↓' : '↑'}</span> }
        </ColTitleHeader>
    );

    const EditableTicket = styled.input`
        border: none;
        font-size: 11px;
        background: none;
        width:56px;
        text-align: center;
    `;

    const JIRALink = styled.a`
        text-align: center;
        text-decoration: underline;
        color: cornflowerblue;
    `;

    const JIRATicket = ({ lesson }) => {
        return lessons.editJIRA === lesson.lessonNode.nodeID || !lesson.lessonNode.JIRATicket || lesson.lessonNode.JIRATicket === '-' ? (
            <EditableTicket
                spellCheck={false} ref={input => { if (input) input.value = lesson.lessonNode.JIRATicket || '-'; }}
                onFocus={e => editingEl.current = e.target} onBlur={e => { fieldChanged(e, lesson, 'JIRATicket'); lessons.update({ editJIRA: '' }); editingEl.current = null; }} />
        ) : (
            <JIRALink
                href={`https://mirinae.atlassian.net/browse/${lesson.lessonNode.JIRATicket}`} target="_blank">
                {lesson.lessonNode.JIRATicket}
            </JIRALink>
        );
    };

    const unlockEdit = async (lesson) => {
        await lessons.releaseEditLock(lesson);
        lesson.editing = false;
        setLessonList(prev => prev.map(lsn => lsn));
    };

    const owner = (lesson) => (lesson.editor && lesson.editor.createdBy && lesson.editor.createdBy._id) === user._id;

    const editors = () => {
        return [{ value: 'none', label: ' ' }]
            .concat(lessons.editors
                .filter(e => e.role.match(/admin|superadmin|lessoneditor|lessoncreator/))
                .filter(e => ![
                    '5e0567c8719c3a82fe32c5ec', // drop non editor admin users
                    '620f3c2597a88e31e280f379',
                    '62fb3a88af5b407c0e1c8016',
                    '63aa5a711143e540ff684fdd',
                    '62c3a684310fbc07d3c282ff',
                    '62bd05f3658edf3598a90b48',
                    '5e58b9786f607c14c9cee98d',
                    '629079f1b568b65288b619fa',
                ].includes(e._id.toString()))
                .map(e => ({ value: e._id, label: e.name.split(' ')[0] }))
                .sort((e1, e2) => (e1.label.toLowerCase() > e2.label.toLowerCase() ? 1 : -1)));
    };

    const editorSelection = (lesson) => (lesson.editor.assignedTo ? lesson.editor.assignedTo._id : 'none');
    const lessonToolTip = (lesson) => `${lesson.replaced ? '** replaced **' : ''}
id=${lesson._id},
guid=${lesson.guid}
created=${shortLocalDate(lesson.created)}
published=${lesson.lessonNode.publishDate ? new Date(lesson.lessonNode.publishDate) : '-not yet-'}
assigned by=${lesson.editor.assignedTo && lesson.editor.assignedTo.assignedBy ? lesson.editor.assignedTo.assignedBy.name : '--'}`;

    return (
        <ListWrapper>
            <OpenClose open={listOpen} onClick={() => { listOpen = !listOpen; setOpen(!open); }}><span>{listOpen ? '' : ''}</span>{`Lesson list - ${apiServer || window.location.origin}`}</OpenClose>
            { listOpen && (
                <>
                    <ListTools>
                        <DisplayFilter />
                        <TextInput
                            label="Search string" onInput={e => setSearchString(e.target.value)}
                            fontSize={14} width="25%" style={{ alignSelf: 'end', marginBottom: 10, marginTop: -25 }}
                        />
                    </ListTools>
                    <List>
                        <ListTable>
                            <ListHeader>
                                <ListRow>
                                    <ColTitle field="title">Title</ColTitle>
                                    <ColTitle field="number" style={{ textAlign: 'center' }}>Lesson #</ColTitle>
                                    <ColTitle field="group">Group</ColTitle>
                                    <ColTitle field="status">Status</ColTitle>
                                    {/*<ColTitle field="jira">JIRA</ColTitle>*/}
                                    <ColTitle field="language">Language</ColTitle>
                                    <ColTitle field="private">Private</ColTitle>
                                    <ColTitle field="archived">Archived</ColTitle>
                                    { isLessonCreator && <ColTitle field="assign">Assigned</ColTitle> }
                                    <ColTitle field="editor">Last editor</ColTitle>
                                    <ColTitle field="updated">Last edited</ColTitle>
                                    <ColTitle field="creator">Creator</ColTitle>
                                    {/* <ColTitle field="created">Created</ColTitle> */}
                                    <ColTitle />
                                </ListRow>
                                {/*<ListRow style={{ height: 2 }} />*/}
                            </ListHeader>
                            <ListBody>
                                { lessonList.filter(displayFilter).map((lesson, i) => (
                                    <ListRow
                                        key={i}
                                        title={lessonToolTip(lesson)}
                                        onMouseEnter={() => { if (!editingEl.current) setHover(lesson._id); }} hover={hover === lesson._id}
                                        onMouseLeave={() => { if (!editingEl.current) { document.activeElement.blur(); if (!lessons.adminListContextMenu.open) setHover(null); } }}
                                    >
                                        <ListFieldWithMenu onContextMenu={e => { document.activeElement.blur(); onContextMenu(e, lesson); }}>
                                            <EditableTitle
                                                lesson={lesson} spellCheck={false} ref={input => { if (input) input.value = lesson.title; }}
                                                readOnly={!isAdmin && lesson.lessonNode.status.match(/Published|Coming soon/)}
                                                onFocus={e => editingEl.current = e.target} onBlur={e => titleChanged(e, lesson)} />
                                        </ListFieldWithMenu>
                                        <ListField style={{ textAlign: 'right', paddingRight: 15 }}>{lesson.lessonNode.number}</ListField>
                                        <ListField><Select selection={lesson.lessonNode.group} options={groupChoices} onChange={e => fieldChanged(e, lesson, 'group')} /></ListField>
                                        <ListField><Select selection={lesson.lessonNode.status} options={statusSelectOptions(user, lesson.lessonNode)} onChange={e => fieldChanged(e, lesson, 'status')} /></ListField>
                                        {/*<ListField><JIRATicket lesson={lesson} /></ListField>*/}
                                        <ListField style={{ textAlign: 'center' }}>{lesson.language?.replace('english', 'en') || 'en'}</ListField>
                                        <ListField><Checkbox type="checkbox" disabled={!owner(lesson) && !isAdmin} checked={lesson.private} onChange={e => flagChanged(e, lesson, 'private')} /></ListField>
                                        <ListField><Checkbox type="checkbox" disabled={!isAdmin || lesson.lessonNode.status.match(/Published|Coming soon/)} checked={lesson.archived} onChange={e => flagChanged(e, lesson, 'archived')} /></ListField>
                                        { isLessonCreator && <ListField><Select selection={editorSelection(lesson)} options={editors()} onChange={e => assignEditor(e, lesson)} /></ListField> }
                                        <ListField style={{ textAlign: 'center' }}>{ ((lesson.editing ? lesson.editing.editor.name : (lesson.editor ? lesson.editor.editedBy.name : '?')) || '').split(' ')[0]}</ListField>
                                        { lesson.editing ? <Editing onClick={() => unlockEdit(lesson)} title={localDate(lesson.updated)}>editing</Editing> : <DateCell>{localDate(lesson.updated)}</DateCell> }
                                        <ListField style={{ textAlign: 'center' }}>{ (lesson.editor && lesson.editor.createdBy ? lesson.editor.createdBy.name : '?').split(' ')[0]}</ListField>
                                        {/* <DateCell>{shortLocalDate(lesson.created)}</DateCell> */}
                                        <Tools>
                                            <ToolButton onClick={() => edit(lesson)} title="Edit"><EditIcon /></ToolButton>
                                            {/*<ToolButton onClick={() => conform(lesson)} title="Conform to Figma template"><FormatPaintIcon /></ToolButton>*/}
                                            {/*<ToolButton onClick={() => lessons.reset(lesson)} title="Reset"><AutorenewOutlinedIcon /></ToolButton>*/}
                                            {/*<ToolButton onClick={() => lessons.backup(lesson)} title="Backup"><SaveIcon /></ToolButton>*/}
                                            {/*<ToolButton onClick={() => lessons.download(lesson)} title="Download"><CloudDownloadIcon /></ToolButton>*/}
                                            <ToolButton onClick={() => lessons.play(lesson)} title="Play"><PlayArrowIcon /></ToolButton>
                                        </Tools>
                                    </ListRow>
                                ))}
                            </ListBody>
                        </ListTable>
                    </List>
                    { lessonList.length === 0 && lessons.initializing && (
                        <EmptyList style={{ height: 100, transform: 'scale(0.8)' }}><RotationSpinner /></EmptyList>
                    )}
                </>
            )}
        </ListWrapper>
    );
};

export default LessonList;
